import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authService } from '../services/auth.service'
import { toast } from 'react-toastify'
import { registerNavigationCallback } from '../utils/authUtils'

export default function TwoFactorVerifyPage() {
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const userId = useLocation().state?.userId
  const otpKeys = ['otp-1', 'otp-2', 'otp-3', 'otp-4', 'otp-5', 'otp-6'];

  // Register navigation callback
  useEffect(() => {
    registerNavigationCallback(navigate);
  }, [navigate]);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').toUpperCase()
    const pastedValue = pastedData.slice(0, 6)
    
    if (pastedValue.length === 6) {
      const newOtp = pastedValue.split('')
      setOtp(newOtp)
      
      // Focus the last input
      const inputs = document.querySelectorAll('input[type="text"]')
      if (inputs[5]) {
        (inputs[5] as HTMLInputElement).focus()
      }
    }
  }

  const handleChange = (element: HTMLInputElement, index: number) => {
    const value = element.value.toUpperCase()
    setOtp([...otp.map((d, idx) => (idx === index ? value : d))])

    // Focus next input
    if (value !== '') {
      const nextInput = element.nextElementSibling as HTMLInputElement
      if (nextInput) {
        nextInput.focus()
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key !== 'Backspace') return;
    
    const currentInput = e.target as HTMLInputElement;
    const isEmpty = currentInput.value === '';
    const newOtp = [...otp];
    
    if (isEmpty && index > 0) {
      const prevInput = currentInput.previousElementSibling as HTMLInputElement;
      prevInput?.focus();
      newOtp[index - 1] = '';
    } else {
      newOtp[index] = '';
    }
    
    setOtp(newOtp);
  }

  const handleVerify = async () => {
    try {
      setLoading(true)
      setError('')
      
      if (!userId) {
        setError('User ID not found. Please try logging in again.')
        return
      }

      const otpString = otp.join('')
      if (otpString.length !== 6) {
        setError('Please enter all 6 digits')
        return
      }

      const response = await authService.verifyOtp(userId, otpString)
      
      if (!response.accessToken) {
        setError(response.message || 'Invalid verification code')
        toast.error(response.message || 'Invalid verification code')
        return
      }

      // Show success toast - navigation is now handled by the auth service
      if(response.accessToken){ 
        toast.success('Two-factor authentication successful! Welcome back.')
      }

    } catch (err: any) {
      setError(err.message || 'Failed to verify code')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    try {
      setLoading(true)
      setError('')
      
      if (!userId) {
        setError('User ID not found. Please try logging in again.')
        return
      }

      await authService.generateOtp(userId)
      // Clear existing OTP inputs
      setOtp(['', '', '', '', '', ''])
    } catch (err: any) {
      setError(err.message || 'Failed to resend code')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-primary relative flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-cover bg-[position:0%_50%] mix-blend-luminosity opacity-[0.08] rotate-180 auth-background" 
      />
      <div className="bg-white rounded-card shadow-lg w-full max-w-card p-card-padding pb-card-padding-bottom gap-section-gap relative z-10">
        <div className="flex flex-col items-center mb-8">
          <h1 className="text-heading font-heading text-text-primary text-center">Two-Factor Authentication</h1>
          <p className="text-text-secondary text-subtext font-normal text-center">Enter the One-Time-Password sent to your email</p>
        </div>


        <div className="text-center mb-4">
          <h2 className="text-[14px] font-normal text-[#767A81]">One-Time-Password</h2>
        </div>


        <div className="space-y-6">
          <div className="flex justify-center space-x-3">
            {otp.map((digit, index) => (
              <input
                key={otpKeys[index]}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(e.target, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={index === 0 ? handlePaste : undefined}
                id={`otp-input-${index}`}
                className="w-12 h-12 text-center text-xl border border-border rounded-input shadow-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary uppercase"
              />
            ))}
          </div>

          {error && (
            <p className="text-center text-small text-red-600" id="error-message">{error}</p>
          )}

          <button
            type="button"
            className="btn-primary w-full"
            onClick={handleVerify}
            disabled={loading}
            id="verify-button"
          >
            {loading ? 'Verifying...' : 'Submit'}
          </button>

          <div className="text-center">
            <span className="text-[16px] text-text-secondary">
             Did not receive OTP?{' '}
              <button
                type="button"
                className="text-small text-text-secondary underline"
                onClick={handleResend}
                disabled={loading}
                data-testid="resend-button"
              >
                Resend Link
              </button>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
